/**
 * AppEdge - Custom edge for voice app connections
 *
 * Renders different styles based on edge type:
 * - Navigation (violet): Shows voice trigger phrase
 * - Agent Trigger (amber): Shows agent connection
 * - Data Binding (green): Shows data flow direction
 *
 * The edge label displays the voice trigger phrase
 * so makers can see how screens are connected.
 *
 * Click the label to open the property editor and customize
 * the voice trigger phrase.
 */

import { memo, useState, useCallback, useRef, useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import type { AppEdgeData, AppEdgeType, AppScreenNodeData } from '@deep-agents/shared-types';
import { useEdgeContext } from './EdgeContext';
import { EdgePropertyPopover } from './EdgePropertyPopover';

// Colors for each edge type (matching the shared-types display info)
const EDGE_COLORS: Record<AppEdgeType | 'default', string> = {
  navigation: '#8B5CF6',    // Violet
  'agent-trigger': '#F59E0B', // Amber
  'data-binding': '#10B981',  // Green
  default: '#94A3B8',         // Gray (fallback)
};

// Icons for each edge type
const EDGE_ICONS: Record<AppEdgeType, string> = {
  navigation: '🗣️',
  'agent-trigger': '🤖',
  'data-binding': '🔗',
};

/**
 * Helper to safely extract AppEdgeData from edge.data
 * React Flow edge data is typed as Record<string, unknown>
 */
function getAppEdgeData(data: Record<string, unknown> | undefined): AppEdgeData | null {
  if (!data || typeof data.type !== 'string') return null;
  return data as unknown as AppEdgeData;
}

export const AppEdge = memo(function AppEdge(props: EdgeProps) {
  const {
    id,
    source,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    data: rawData,
    selected,
  } = props;

  // Access edge update function and nodes from context
  const { updateEdge, nodes } = useEdgeContext();

  // State for property popover
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const labelRef = useRef<HTMLDivElement>(null);

  // Get the edge path
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  // Safely extract our custom edge data
  const data = getAppEdgeData(rawData);

  // Get button widgets from the source node (for the widget selector in the popover)
  const sourceNodeButtons = useMemo(() => {
    const sourceNode = nodes.find((n) => n.id === source);
    if (!sourceNode || sourceNode.type !== 'appScreen') return [];
    const nodeData = sourceNode.data as AppScreenNodeData;
    return (nodeData.widgets || []).filter((w) => w.type === 'button');
  }, [nodes, source]);

  // Determine edge type and color
  const edgeType: AppEdgeType = data?.type || 'navigation';
  const color = EDGE_COLORS[edgeType] || EDGE_COLORS.default;
  const icon = EDGE_ICONS[edgeType] || '➡️';

  // Get the label to display
  let label = '';
  if (data?.type === 'navigation') {
    label = data.triggerPhrase ? `"${data.triggerPhrase}"` : 'Click to set trigger';
  } else if (data?.type === 'agent-trigger') {
    label = data.triggerPhrase ? `"${data.triggerPhrase}"` : 'Click to set trigger';
  } else if (data?.type === 'data-binding') {
    label = `${data.sourceField} → ${data.targetField}`;
  }

  // Handle label click - open popover
  const handleLabelClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (labelRef.current) {
      const rect = labelRef.current.getBoundingClientRect();
      setPopoverPosition({
        x: rect.left + rect.width / 2,
        y: rect.top,
      });
    }
    setShowPopover(true);
  }, []);

  // Handle popover update
  const handlePopoverUpdate = useCallback(
    (newData: AppEdgeData) => {
      updateEdge(id, newData as unknown as Record<string, unknown>);
    },
    [id, updateEdge]
  );

  // Handle popover close
  const handlePopoverClose = useCallback(() => {
    setShowPopover(false);
  }, []);

  return (
    <>
      {/* The edge path itself */}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...(typeof style === 'object' ? style : {}),
          stroke: color,
          strokeWidth: selected ? 3 : 2,
        }}
      />

      {/* Edge label - click to edit */}
      <EdgeLabelRenderer>
        <div
          ref={labelRef}
          className="nodrag nopan pointer-events-auto"
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            // Small padding, rounded corners, white background
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: 'white',
            border: `2px solid ${color}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontSize: '11px',
            fontWeight: 500,
            color: color,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
          }}
          onClick={handleLabelClick}
          title="Click to edit voice trigger"
        >
          <span style={{ marginRight: '4px' }}>{icon}</span>
          {label}
        </div>
      </EdgeLabelRenderer>

      {/* Property popover — must be inside EdgeLabelRenderer to escape SVG context */}
      {showPopover && data && (
        <EdgeLabelRenderer>
          <EdgePropertyPopover
            data={data}
            onUpdate={handlePopoverUpdate}
            onClose={handlePopoverClose}
            position={popoverPosition}
            sourceButtons={sourceNodeButtons}
          />
        </EdgeLabelRenderer>
      )}
    </>
  );
});

// Default export for the edge types registry
export default AppEdge;
